import { useParams } from "react-router";
import {
  useHtzWorkOrderShowQuery,
  useHtzWorkOrderUpdateWorkOrderStateMutation,
} from "@/services/backend/htz/work-order/api/work-order-api";
import { WorkOrderStateDropdown } from "@/services/backend/relationships/components/work-order-state-dropdown";
import t from "@/lang/lang";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { WorkOrderPdf } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/work-order-pdf";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { SignatureButton } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/signature-button";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { WorkOrderProviders } from "@/routes/processes/[processId]/htz/work-orders/_components/work-order-providers";
import { useWorkOrderStateGuard } from "@/routes/processes/[processId]/htz/work-orders/_components/use-work-order-state-guard";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import { Separator } from "@/shared/components/ui/separator";
import { WorkOrder } from "@/services/backend/htz/work-order/types/work-order";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { WorkOrderBreadcrumbs } from "@/routes/processes/[processId]/htz/work-orders/_components/work-order-breadcrumbs";

export function WorkOrderExecutionRoute() {
  const { workOrderId } = useParams();

  return (
    <WorkOrderProviders>
      <WorkOrderExecutionPage workOrderId={workOrderId!} />
    </WorkOrderProviders>
  );
}

function WorkOrderExecutionPage({ workOrderId }: { workOrderId: string }) {
  const {
    data: workOrder,
    isLoading,
    error,
  } = useHtzWorkOrderShowQuery({
    id: workOrderId,
  });

  useWorkOrderStateGuard(workOrder, FulfillmentState.Execution);

  const breadcrumbs = workOrder && (
    <WorkOrderBreadcrumbs workOrder={workOrder} segment="execution">
      {t("Ausführung")}
    </WorkOrderBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error ? <RTKQueryErrorAlert error={error} /> : null}
      {isLoading && <DocumentPageSkeleton />}
      {workOrder && <Page workOrder={workOrder} />}
    </ProcessPageMainTemplate>
  );
}

function Page({ workOrder }: { workOrder: WorkOrder }) {
  const links = [
    {
      to: "data",
      label: t("Arbeitsschein"),
      fieldName: [
        "referenceNumber",
        "executingBranchId",
        "beginDate",
        "endDate",
        "teamLeader",
        "internalComment",
        "address",
        "contact",
        "representative",
      ],
    },
    {
      to: "positions",
      label: t("Leistungen"),
      fieldName: ["positions"],
    },
    {
      to: "laboratory",
      label: t("Labor"),
    },
    {
      to: "files",
      label: t("Dateien"),
    },
  ];

  return (
    <DocumentPage>
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              fieldName={link.fieldName}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <Separator orientation="vertical" className="max-h-8" />
        <DocumentPageActions>
          <PersistenceStateUsingContext className="mr-2" />
          <SignatureButton workOrderId={workOrder.id} />
          <Separator orientation="vertical" className="min-h-8" />
          <WorkOrderPdf workOrder={workOrder} />
          <Separator orientation="vertical" className="min-h-8" />
          <WorkOrderStateDropdown
            workOrder={workOrder}
            mutation={useHtzWorkOrderUpdateWorkOrderStateMutation}
            transitions={[
              FulfillmentState.Planning,
              FulfillmentState.Executed,
              FulfillmentState.PartiallyExecuted,
            ]}
          />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
